import React, { Suspense, lazy, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
} from "react-router-dom";
import {
  GoogleOAuthProvider,
  GoogleLogin,
  CredentialResponse,
} from "@react-oauth/google";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./App.css";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Material-UI Icons
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import BarChartIcon from "@mui/icons-material/BarChart";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ScheduleIcon from "@mui/icons-material/Schedule";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import DashboardIcon from "@mui/icons-material/Dashboard";
import LogoutIcon from "@mui/icons-material/Logout";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ShareIcon from "@mui/icons-material/Share";
import PeopleIcon from "@mui/icons-material/People";
import DynamicFeedIcon from "@mui/icons-material/DynamicFeed";
import InsightsIcon from "@mui/icons-material/Insights";
import InstagramIcon from "@mui/icons-material/Instagram";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import CloudSyncIcon from "@mui/icons-material/CloudSync";
import HistoryIcon from "@mui/icons-material/History";
import SettingsIcon from "@mui/icons-material/Settings";
import LanguageIcon from "@mui/icons-material/Language";
import SearchIcon from "@mui/icons-material/Search";
import StorageIcon from "@mui/icons-material/Storage";
import MenuIcon from "@mui/icons-material/Menu";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";

// Import Sidebar and PageLoader (non-lazy, always needed)
import Sidebar from "./components/Sidebar";
import PageLoader from "./components/PageLoader";
import ErrorBoundary from "./components/ErrorBoundary";
import { ToastProvider } from "./contexts/ToastContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { LightboxProvider } from "./contexts/LightboxContext";

// Theme toggle icons
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";

// Lazy load route components for better performance
// This reduces initial bundle size by ~70%
const MessagesReport = lazy(() => import("./components/MessagesReport"));
const SummaryReport = lazy(() => import("./components/SummaryReport"));
const JobsManagement = lazy(() => import("./components/JobsManagement"));
const JobDetails = lazy(() => import("./components/JobDetails"));
const ScheduledJobsManagement = lazy(
  () => import("./components/ScheduledJobsManagement")
);
const DataSourcesManagement = lazy(
  () => import("./components/DataSourcesManagement")
);
const MediaManagement = lazy(() => import("./components/MediaManagement"));
const MediaDetails = lazy(() => import("./components/MediaDetails"));
const SocialHub = lazy(() => import("./components/SocialHub"));
const _RelatedContentAdmin = lazy(
  () => import("./components/RelatedContentAdmin")
);
const ChatInterface = lazy(() => import("./components/ChatInterface"));
const XAccountsHub = lazy(() => import("./components/XAccountsHub"));
const SocialFeed = lazy(() => import("./pages/SocialFeed"));
const SocialAnalytics = lazy(() => import("./pages/SocialAnalytics"));
const InstagramConnect = lazy(() => import("./components/InstagramConnect"));
const UnifiedSearchDebugger = lazy(
  () => import("./components/UnifiedSearchDebugger")
);
const WebsiteWhitelist = lazy(
  () => import("./components/Settings/WebsiteWhitelist")
);
const BackfillJobs = lazy(() => import("./components/BackfillJobs"));
const DBInspector = lazy(() => import("./pages/DBInspector"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const CostAnalytics = lazy(() => import("./pages/CostAnalytics"));
const NotificationSettings = lazy(() => import("./pages/NotificationSettings"));
const UserManagement = lazy(() => import("./components/UserManagement"));

// Dashboard card/section data types
interface DashboardCard {
  path: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  variant?: "primary" | "secondary" | "tertiary";
}

interface DashboardSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  cards: DashboardCard[];
}

// Dashboard Home Component - Professional Hero Design with Permission Filtering
const DashboardHome: React.FC = () => {
  const { hasPageAccess } = useAuth();

  // Featured cards configuration
  const featuredCards: DashboardCard[] = useMemo(
    () => [
      {
        path: "/chat",
        title: "BrightMind AI",
        description: "Query the knowledge base with AI assistance",
        icon: <AutoAwesomeIcon />,
        variant: "primary",
      },
      {
        path: "/social/accounts",
        title: "Accounts Hub",
        description: "Manage social accounts and sync posts",
        icon: <PeopleIcon />,
        variant: "secondary",
      },
      {
        path: "/social/feed",
        title: "Social Feed",
        description: "Browse posts from trusted accounts",
        icon: <DynamicFeedIcon />,
        variant: "tertiary",
      },
    ],
    []
  );

  // Sections configuration
  const sections: DashboardSection[] = useMemo(
    () => [
      {
        id: "social",
        title: "Social",
        icon: <ShareIcon className="section-icon" />,
        cards: [
          {
            path: "/social/accounts",
            title: "Accounts Hub",
            description: "Manage accounts, sync posts, chat with AI personas",
            icon: <PeopleIcon />,
          },
          {
            path: "/social/feed",
            title: "Feed",
            description: "Browse posts from Here4Good and trusted accounts",
            icon: <DynamicFeedIcon />,
          },
          {
            path: "/social/analytics",
            title: "Analytics",
            description: "Social media performance dashboard",
            icon: <TrendingUpIcon />,
          },
          {
            path: "/social/instagram",
            title: "Instagram",
            description: "Connect and manage Instagram accounts",
            icon: <InstagramIcon />,
          },
          {
            path: "/social/feed-analysis",
            title: "Feed Analysis",
            description: "Analyze your social media feed with AI",
            icon: <InsightsIcon />,
          },
        ],
      },
      {
        id: "ai-usage",
        title: "AI Usage",
        icon: <BarChartIcon className="section-icon" />,
        cards: [
          {
            path: "/ai-usage/overview",
            title: "Overview",
            description: "System-wide analytics and usage statistics",
            icon: <AssessmentIcon />,
          },
          {
            path: "/ai-usage/messages",
            title: "Messages",
            description: "View user conversations and AI responses",
            icon: <ChatBubbleOutlineIcon />,
          },
          {
            path: "/ai-usage/costs",
            title: "Cost Analytics",
            description: "API cost tracking, projections, and simulator",
            icon: <AttachMoneyIcon />,
          },
        ],
      },
      {
        id: "knowledge",
        title: "Knowledge Base",
        icon: <MenuBookIcon className="section-icon" />,
        cards: [
          {
            path: "/knowledge/documents",
            title: "Document Library",
            description: "Browse and manage indexed documents",
            icon: <LibraryBooksIcon />,
          },
          {
            path: "/knowledge/sources",
            title: "Data Sources",
            description: "Documents and X accounts for RAG",
            icon: <FolderOpenIcon />,
          },
          {
            path: "/knowledge/indexing",
            title: "Indexing Jobs",
            description: "Monitor document processing and indexing",
            icon: <CloudSyncIcon />,
          },
          {
            path: "/knowledge/scheduled",
            title: "Scheduled Jobs",
            description: "Manage automated recurring tasks",
            icon: <ScheduleIcon />,
          },
          {
            path: "/knowledge/backfill",
            title: "Backfill Jobs",
            description: "Manage async X account backfill operations",
            icon: <HistoryIcon />,
          },
        ],
      },
      {
        id: "system",
        title: "System",
        icon: <SettingsIcon className="section-icon" />,
        cards: [
          {
            path: "/system/users",
            title: "User Management",
            description: "Manage user labels and auto-labeling rules",
            icon: <PeopleIcon />,
          },
          {
            path: "/system/whitelist",
            title: "Website Whitelist",
            description: "Manage trusted domains for web search",
            icon: <LanguageIcon />,
          },
          {
            path: "/system/search",
            title: "Search Debugger",
            description: "Test and debug RAG search queries",
            icon: <SearchIcon />,
          },
          {
            path: "/system/db",
            title: "DB Inspector",
            description: "Inspect Firestore collections and documents",
            icon: <StorageIcon />,
          },
        ],
      },
    ],
    []
  );

  // Filter featured cards based on permissions
  const accessibleFeaturedCards = useMemo(
    () => featuredCards.filter((card) => hasPageAccess(card.path)),
    [featuredCards, hasPageAccess]
  );

  // Filter sections and their cards based on permissions
  const accessibleSections = useMemo(
    () =>
      sections
        .map((section) => ({
          ...section,
          cards: section.cards.filter((card) => hasPageAccess(card.path)),
        }))
        .filter((section) => section.cards.length > 0),
    [sections, hasPageAccess]
  );

  return (
    <div className="dashboard-home">
      {/* Quick Access Section */}
      {accessibleFeaturedCards.length > 0 && (
        <div className="quick-access-section">
          <h2 className="page-title">Dashboard</h2>
          <div className="featured-cards">
            {accessibleFeaturedCards.map((card) => (
              <Link
                key={card.path}
                to={card.path}
                className={`featured-card featured-card-${
                  card.variant || "primary"
                }`}
              >
                <div className="featured-card-icon">{card.icon}</div>
                <div className="featured-card-content">
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </div>
                <div className="featured-card-arrow">→</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Sections */}
      {accessibleSections.map((section) => (
        <div key={section.id} className="services-section">
          <div className="section-header">
            {section.icon}
            <h2 className="section-title">{section.title}</h2>
          </div>
          <div className="services-grid">
            {section.cards.map((card) => (
              <Link key={card.path} to={card.path} className="service-card">
                <div className="service-card-icon">{card.icon}</div>
                <h3 className="service-card-title">{card.title}</h3>
                <p className="service-card-description">{card.description}</p>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
DashboardHome.displayName = "DashboardHome";

// Replace with your Google Client ID from GCP Console > APIs & Services > Credentials
const GOOGLE_CLIENT_ID =
  "1086614802573-fafec4ri80gn8p8um6s84uau5fplfp5n.apps.googleusercontent.com";

// User interface is now managed by AuthContext

// Theme Toggle Button Component
const ThemeToggle: React.FC = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <button
      className="btn-theme-toggle"
      onClick={toggleTheme}
      title={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}
    >
      {theme === "dark" ? (
        <LightModeIcon className="icon-md" />
      ) : (
        <DarkModeIcon className="icon-md" />
      )}
    </button>
  );
};

// User Menu Component with dropdown
const UserMenu: React.FC = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { theme, toggleTheme } = useTheme();
  const { user, logout, permissions } = useAuth();
  const menuRef = React.useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!user) return null;

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        className="user-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
      >
        <div className="user-avatar">{user.name?.charAt(0).toUpperCase()}</div>
        <span className="user-name">{user.name}</span>
        <KeyboardArrowDownIcon
          className={`user-menu-arrow ${isOpen ? "open" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          <div className="user-menu-header">
            <div className="user-menu-avatar">
              {user.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-menu-info">
              <span className="user-menu-name">{user.name}</span>
              {user.email && (
                <span className="user-menu-email">{user.email}</span>
              )}
              {permissions && (
                <span className="user-menu-role">{permissions.role_name}</span>
              )}
            </div>
          </div>
          <div className="user-menu-divider" />
          <button
            className="user-menu-item"
            onClick={() => {
              toggleTheme();
              setIsOpen(false);
            }}
          >
            {theme === "dark" ? (
              <LightModeIcon className="text-md" />
            ) : (
              <DarkModeIcon className="text-md" />
            )}
            <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
          </button>
          <button
            className="user-menu-item user-menu-item-danger"
            onClick={() => {
              logout();
              setIsOpen(false);
            }}
          >
            <LogoutIcon className="text-md" />
            <span>Sign Out</span>
          </button>
        </div>
      )}
    </div>
  );
};

// Create QueryClient instance for React Query
// This provides caching, refetching, and state management for API calls
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // Data is fresh for 1 minute (default for most queries)
      gcTime: 600000, // Keep in cache for 10 minutes (increased from 5 for better UX)
      refetchOnWindowFocus: false, // Disabled by default, enabled per-query where needed
      refetchOnMount: true, // Always refetch on mount for fresh data
      refetchOnReconnect: true, // Refetch when connection restored
      refetchIntervalInBackground: false, // Pause polling when tab is not visible (saves resources)
      retry: 2, // Retry failed requests twice
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      networkMode: "online", // Only run queries when online
    },
    mutations: {
      retry: 1, // Retry failed mutations once
      retryDelay: 1000, // Wait 1 second before retrying
      networkMode: "online", // Only run mutations when online
    },
  },
});

// Inner App Content that uses AuthContext
const AppContent: React.FC = () => {
  const { user, login, isLoading } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLoginSuccess = async (response: CredentialResponse) => {
    if (response.credential) {
      try {
        await login(response.credential);
      } catch (error) {
        console.error("Login failed:", error);
      }
    }
  };

  const handleLoginFailure = () => {
    console.error("Login failed");
  };

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="login-container">
        <div className="login-card">
          <DashboardIcon className="login-icon" />
          <h1 className="login-title">Loading...</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {user ? (
        <>
          <div className="app-layout">
            <Sidebar
              isOpen={isMobileMenuOpen}
              onClose={handleMobileMenuClose}
            />

            <div className="app-main">
              <header className="dashboard-header">
                <div className="header-content">
                  {/* Mobile Menu Toggle */}
                  <button
                    className="mobile-menu-toggle hide-desktop"
                    onClick={handleMobileMenuToggle}
                    aria-label="Open navigation menu"
                    aria-expanded={isMobileMenuOpen}
                  >
                    <MenuIcon />
                  </button>

                  <Link to="/" className="header-brand">
                    <h1 className="dashboard-title">
                      BrightMind <span className="title-accent">AI</span>
                    </h1>
                    <span className="header-app-name hide-mobile">
                      Back Office
                    </span>
                    <span className="header-divider">·</span>
                    <span className="header-subtitle hide-mobile">
                      by Here4Good
                    </span>
                  </Link>
                  <div className="header-actions">
                    <ThemeToggle />
                    <UserMenu />
                  </div>
                </div>
              </header>

              <main className="dashboard-container">
                <Suspense
                  fallback={
                    <PageLoader
                      message="Loading page..."
                      submessage="Please wait"
                    />
                  }
                >
                  <Routes>
                    <Route path="/" element={<DashboardHome />} />

                    {/* AI Chat - wrapped in fullscreen layout */}
                    {/* Supports optional thread ID for shareable/bookmarkable conversations */}
                    <Route
                      path="/chat/:threadId?"
                      element={
                        <ProtectedRoute requiredPath="/chat">
                          <ErrorBoundary>
                            <div className="page-fullscreen-chat">
                              <ChatInterface />
                            </div>
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />

                    {/* Social Section */}
                    <Route
                      path="/social/accounts/*"
                      element={
                        <ProtectedRoute requiredPath="/social/accounts">
                          <ErrorBoundary>
                            <XAccountsHub />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/social/feed"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <SocialFeed />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/social/analytics"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <SocialAnalytics />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/social/feed-analysis"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <SocialHub />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/social/instagram"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <InstagramConnect />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />

                    {/* AI Usage Section */}
                    <Route
                      path="/ai-usage/overview"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <SummaryReport />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ai-usage/messages"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <MessagesReport />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ai-usage/costs"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <CostAnalytics />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />

                    {/* Knowledge Base Section */}
                    <Route
                      path="/knowledge/sources"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <DataSourcesManagement />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/knowledge/documents"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <MediaManagement />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/knowledge/documents/:docId"
                      element={
                        <ProtectedRoute requiredPath="/knowledge/documents">
                          <ErrorBoundary>
                            <MediaDetails />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/knowledge/indexing"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <JobsManagement />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/knowledge/indexing/:jobId"
                      element={
                        <ProtectedRoute requiredPath="/knowledge/indexing">
                          <ErrorBoundary>
                            <JobDetails />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/knowledge/scheduled"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <ScheduledJobsManagement />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/knowledge/backfill"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <BackfillJobs />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />

                    {/* System Section */}
                    <Route
                      path="/system/notifications"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <NotificationSettings />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/system/whitelist"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <WebsiteWhitelist />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/system/users"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <UserManagement />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/system/search"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <UnifiedSearchDebugger />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/system/db"
                      element={
                        <ProtectedRoute>
                          <ErrorBoundary>
                            <DBInspector />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin Section */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute adminOnly>
                          <ErrorBoundary>
                            <AdminDashboard />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />

                    {/* Legacy routes - redirect to new paths */}
                    <Route
                      path="/x-accounts/*"
                      element={<Navigate to="/social/accounts" replace />}
                    />
                    <Route
                      path="/social-feed"
                      element={<Navigate to="/social/feed" replace />}
                    />
                    <Route
                      path="/social"
                      element={<Navigate to="/social/accounts" replace />}
                    />
                    <Route
                      path="/social/analysis"
                      element={<Navigate to="/social/feed-analysis" replace />}
                    />
                    <Route
                      path="/instagram"
                      element={<Navigate to="/social/instagram" replace />}
                    />
                    <Route
                      path="/messages"
                      element={<Navigate to="/ai-usage/messages" replace />}
                    />
                    <Route
                      path="/analytics/social"
                      element={<Navigate to="/social/analytics" replace />}
                    />
                    <Route
                      path="/analytics/usage"
                      element={<Navigate to="/ai-usage/overview" replace />}
                    />
                    <Route
                      path="/analytics/messages"
                      element={<Navigate to="/ai-usage/messages" replace />}
                    />
                    <Route
                      path="/analytics"
                      element={<Navigate to="/ai-usage/overview" replace />}
                    />
                    <Route
                      path="/datasources"
                      element={<Navigate to="/knowledge/sources" replace />}
                    />
                    <Route
                      path="/documents"
                      element={<Navigate to="/knowledge/documents" replace />}
                    />
                    <Route
                      path="/documents/:docId"
                      element={
                        <ProtectedRoute requiredPath="/knowledge/documents">
                          <ErrorBoundary>
                            <MediaDetails />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/jobs"
                      element={<Navigate to="/knowledge/indexing" replace />}
                    />
                    <Route
                      path="/jobs/:jobId"
                      element={
                        <ProtectedRoute requiredPath="/knowledge/indexing">
                          <ErrorBoundary>
                            <JobDetails />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/scheduled-jobs"
                      element={<Navigate to="/knowledge/scheduled" replace />}
                    />
                    <Route
                      path="/backfill-jobs"
                      element={<Navigate to="/knowledge/backfill" replace />}
                    />
                    <Route
                      path="/settings/whitelist"
                      element={<Navigate to="/system/whitelist" replace />}
                    />
                    <Route
                      path="/dev/search"
                      element={
                        <ProtectedRoute requiredPath="/system/search">
                          <ErrorBoundary>
                            <UnifiedSearchDebugger />
                          </ErrorBoundary>
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </main>
            </div>
          </div>
        </>
      ) : (
        <div className="login-container">
          <div className="login-card">
            <DashboardIcon className="login-icon" />
            <h1 className="login-title">Brightmind AI Analytics</h1>
            <p className="login-subtitle">Back Office Dashboard</p>
            <GoogleLogin
              onSuccess={handleLoginSuccess}
              onError={handleLoginFailure}
            />
          </div>
        </div>
      )}
    </div>
  );
};

// Main App wrapper with all providers
function App() {
  return (
    <ThemeProvider>
      <ToastProvider>
        <LightboxProvider>
          <QueryClientProvider client={queryClient}>
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <AuthProvider>
                <Router>
                  <AppContent />
                </Router>
              </AuthProvider>
            </GoogleOAuthProvider>
          </QueryClientProvider>
        </LightboxProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
